import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { InstallButton } from '@/context/InstallContext';

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_vehicle-maintenance-21/artifacts/76wdsgvy_WhatsApp%20Image%202025-12-16%20at%2017.28.15.jpeg" 
                alt="Excel Era Auto Care" 
                className="h-12 w-12 object-contain"
              />
              <span className="text-2xl font-heading font-bold text-foreground">EXCEL ERA AUTO CARE</span>
            </div>
            <div className="flex items-center space-x-4">
              <InstallButton variant="landing" size="sm" />
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
                data-testid="nav-login-btn"
                className="font-bold uppercase tracking-wide"
              >
                Login
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                data-testid="nav-register-btn"
                className="btn-primary"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-foreground leading-tight">
                Premium Auto Care
                <span className="block text-secondary">At Your Doorstep</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-medium">
                Expert oil changes, filter replacements, and service interval resets. Professional technicians come to your homes, offices or locations of your convenience in FCT.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => navigate('/register')}
                  data-testid="hero-get-started-btn"
                  className="btn-primary text-lg h-14"
                >
                  Book Your Service
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-contact-btn"
                  className="btn-secondary text-lg h-14"
                >
                  Contact Us
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-hard border-4 border-foreground">
                <img 
                  src="https://customer-assets.emergentagent.com/job_0701f6b7-d1eb-4272-8880-661fee71510f/artifacts/2vwg75qz_professional-mechanics-talking-in-automotive-shop.jpg" 
                  alt="Professional mechanics in automotive shop"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Our Services
            </h2>
            <p className="text-xl text-muted-foreground font-medium">Comprehensive auto care solutions</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Oil Change', desc: 'Premium oil and filter replacement for optimal engine performance' },
              { title: 'Filter Replacement', desc: 'Air, fuel, and cabin filter changes to keep your vehicle running smoothly' },
              { title: 'Service Interval Reset', desc: 'Professional diagnostic and maintenance indicator reset' }
            ].map((service, idx) => (
              <div key={idx} className="bg-card border-2 border-border p-8 rounded-sm hover:shadow-hard transition-all hover:-translate-y-1">
                <div className="w-16 h-16 bg-primary rounded-sm flex items-center justify-center mb-6">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground font-medium">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Simple Pricing
            </h2>
            <p className="text-xl text-muted-foreground font-medium">Transparent rates with no hidden fees</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white border-4 border-primary p-8 rounded-sm shadow-hard">
              <div className="text-center">
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">First Vehicle</h3>
                <div className="my-6">
                  <span className="text-5xl font-heading font-black text-primary font-mono">₦10,000</span>
                </div>
                <p className="text-muted-foreground font-medium">Complete service package for your first registered vehicle</p>
              </div>
            </div>
            <div className="bg-white border-4 border-secondary p-8 rounded-sm shadow-hard">
              <div className="text-center">
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Additional Vehicles</h3>
                <div className="my-6">
                  <span className="text-5xl font-heading font-black text-secondary font-mono">₦8,000</span>
                </div>
                <p className="text-muted-foreground font-medium">Discounted rate for each additional vehicle</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              How It Works
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Register', desc: 'Create your account in minutes' },
              { step: '02', title: 'Add Vehicles', desc: 'Register all your cars' },
              { step: '03', title: 'Book Service', desc: 'Schedule at your convenience' },
              { step: '04', title: 'Get Serviced', desc: 'Our technicians come to you' }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="w-20 h-20 bg-primary text-white rounded-sm flex items-center justify-center text-3xl font-heading font-black mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-heading font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground font-medium">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-foreground text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl lg:text-5xl font-heading font-bold mb-8">
              Get In Touch
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div>
                <h3 className="font-heading font-bold text-xl mb-2">Phone</h3>
                <p className="font-mono text-lg">+234 9163727713</p>
                <p className="font-mono text-lg">+234 907 676 4071</p>
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl mb-2">Location</h3>
                <p className="text-lg">FCT, Nigeria</p>
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl mb-2">Hours</h3>
                <p className="text-lg">Mon - Sat: 8AM - 6PM</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Our Network Section */}
      <section className="py-16 bg-accent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Join Our Network
            </h2>
            <p className="text-lg text-muted-foreground font-medium">Partner with Excel Era Auto Care</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {/* Merchant Portal */}
            <div className="bg-white border-2 border-border p-6 rounded-sm text-center hover:shadow-hard transition-all">
              <div className="w-14 h-14 bg-amber-500 rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">Merchants</h3>
              <p className="text-sm text-muted-foreground mb-4">Supply oil and filters to our network</p>
              <div className="space-y-2">
                <Link to="/merchant/register">
                  <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold" size="sm">
                    Register
                  </Button>
                </Link>
                <Link to="/merchant/login">
                  <Button variant="outline" className="w-full font-bold border-2" size="sm">
                    Login
                  </Button>
                </Link>
              </div>
            </div>

            {/* Technician Portal */}
            <div className="bg-white border-2 border-border p-6 rounded-sm text-center hover:shadow-hard transition-all">
              <div className="w-14 h-14 bg-blue-600 rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">Technicians</h3>
              <p className="text-sm text-muted-foreground mb-4">Join our expert service team</p>
              <div className="space-y-2">
                <Link to="/technician/register">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold" size="sm">
                    Register
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full font-bold border-2" size="sm">
                    Login
                  </Button>
                </Link>
              </div>
            </div>

            {/* Marketer Portal */}
            <div className="bg-white border-2 border-border p-6 rounded-sm text-center hover:shadow-hard transition-all">
              <div className="w-14 h-14 bg-green-600 rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">Marketers</h3>
              <p className="text-sm text-muted-foreground mb-4">Earn ₦500 per referral service</p>
              <div className="space-y-2">
                <Link to="/marketer/register">
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white font-bold" size="sm">
                    Register
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full font-bold border-2" size="sm">
                    Login
                  </Button>
                </Link>
              </div>
            </div>

            {/* Organization Portal */}
            <div className="bg-white border-2 border-border p-6 rounded-sm text-center hover:shadow-hard transition-all">
              <div className="w-14 h-14 bg-purple-600 rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-2">Organizations</h3>
              <p className="text-sm text-muted-foreground mb-4">Manage your fleet vehicles</p>
              <div className="space-y-2">
                <Link to="/organization/register">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold" size="sm">
                    Register
                  </Button>
                </Link>
                <Link to="/organization/login">
                  <Button variant="outline" className="w-full font-bold border-2" size="sm">
                    Login
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground font-medium">
              © 2024 Excel Era Auto Care. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-bold text-muted-foreground hover:text-green-600 transition-colors uppercase tracking-wide"
                data-testid="marketer-portal-link"
              >
                Marketer Portal →
              </button>
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-bold text-muted-foreground hover:text-primary transition-colors uppercase tracking-wide"
                data-testid="secretary-portal-link"
              >
                Secretary Portal →
              </button>
              <button
                onClick={() => navigate('/login')}
                className="text-sm font-bold text-muted-foreground hover:text-secondary transition-colors uppercase tracking-wide"
                data-testid="technician-portal-link"
              >
                Technician Portal →
              </button>
              <button
                onClick={() => navigate('/admin/login')}
                className="text-sm font-bold text-muted-foreground hover:text-secondary transition-colors uppercase tracking-wide"
                data-testid="admin-portal-link"
              >
                Admin Portal →
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}